import React, { Component } from 'react';
import { connect } from "react-redux";
import { Route, Redirect, Link } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Theme, Loader, MetaMenu } from '@simplybenefits/sb-ui-library';
import { isMobile } from 'react-device-detect';
import ArrowDropDownSharp from '@mui/icons-material/ArrowDropDownSharp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GearIcon from '@mui/icons-material/Settings';
import Badge from "@mui/material/Badge";
import { styled } from '@mui/system';

import Avatar from "react-avatar";
import Sidebar from './components/Sidebar';
import { logout, refreshToken } from "./actions/user";
import * as session from './services/session';
import { redirect, checkMobileRedirectToken, setMobileRedirectToken } from './services/helpers';

import logoImage from './assets/logo-full-color-medium.png';
import appRedirectImage from './assets/app-redirect.png';
import waterMarkLogo from "./assets/logo-watermark-color.png";

const PrivateRouteContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Theme.colors.surface,
    [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start'
    },
    height: '100vh',
    overflow: 'hidden'
}));

const DrawerIcon = styled(IconButton)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down(960)]: {
        display: 'block',
        position: 'absolute',
        top: 10,
        left: 5,
        zIndex: 1000
    }
}));

const LogoContainer = styled('div')(({ theme }) => ({
    flex: 0.15,
    [theme.breakpoints.up(2000)]: {
        flex: 0.15
    },
    [theme.breakpoints.down(1900)]: {
        flex: 0.20
    },
    [theme.breakpoints.down(1400)]: {
        flex: 0.35
    },
    [theme.breakpoints.down(1070)]: {
        flex: 0.45
    },
    [theme.breakpoints.down(960)]: {
        display: 'none'
    }
}));

const SidebarContainer = styled('div')(({ theme }) => ({
    flex: 0.15,
    [theme.breakpoints.up(2000)]: {
        flex: 0.15
    },
    [theme.breakpoints.down(1900)]: {
        flex: 0.20
    },
    [theme.breakpoints.down(1400)]: {
        flex: 0.35
    },
    [theme.breakpoints.down(1070)]: {
        flex: 0.45
    },
    [theme.breakpoints.down(960)]: {
        display: 'none'
    }
}));

/**
 *
 * @param {*} props
 * @returns
 */
const DeviceWarningImage = (props) => {

    const WaterMarkedImage = styled('img')(({ theme }) => ({
        position: 'fixed',
        bottom: 0,
        right: 15,
        zIndex: 0,
        [theme.breakpoints.up(2000)]: {
            width: '18%'
        },
        [theme.breakpoints.down(2000)]: {
            width: '18%'
        },
        [theme.breakpoints.down(1400)]: {
            width: '20%'
        },
        [theme.breakpoints.down(1070)]: {
            width: '25%'
        },
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        },
        maxWidth: 500,
        opacity: 0.4
    }));

    const WhiteLabelledMarkedImage = styled('img')(({ theme }) => ({
        position: 'fixed',
        bottom: 15,
        right: 25,
        zIndex: 0,
        [theme.breakpoints.up(2000)]: {
            width: '15%',
            bottom: 40,
            right: 50
        },
        [theme.breakpoints.down(2000)]: {
            width: '17%'
        },
        [theme.breakpoints.down(1400)]: {
            width: '18%'
        },
        [theme.breakpoints.down(1070)]: {
            width: '22%'
        },
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        },
        maxWidth: 500,
        opacity: 0.4
    }));


    return (
        <>
            { 
                props.whitelabel.watermarkColor ? 
                    <WhiteLabelledMarkedImage alt={props.alt} src={ props.whitelabel.watermarkColor }>
                        {props.children}
                    </WhiteLabelledMarkedImage>
                    
                    :
                    <WaterMarkedImage alt={props.alt} src={ props.src }>
                        {props.children}
                    </WaterMarkedImage>
            }

        </>
    );
};


class PrivateRoute extends Component {
    state = {
        loading: true,
        isAuthenticated: false,
        mobileOpen: false,
        anchorEl: null,
        showDeviceWarning: false
    };

    async componentDidMount() {
        const queryParams = new URLSearchParams(window.location.search);
        const ssoToken = queryParams.get("token");

        // if we have a ssoToken then we will store it and refresh it so it gets validated then redirect to dashboard if it's good
        if (ssoToken) {
            await session.setToken(ssoToken);
            this.props.dispatch(refreshToken());
            return;
        }

        const isAuthenticated = await this._isUserAuthenticated();

        if (isAuthenticated) {
            this.setState({
                isAuthenticated,
                loading: false
            });

            if (isMobile) {
                const showWarning = checkMobileRedirectToken();

                if (showWarning) {
                    this.setState({
                        showDeviceWarning: true
                    });
                }
            }
        } else {
            this._logout();
        }
    }

    handleDrawerToggle = () => {
        this.setState((state) => ({ mobileOpen: !state.mobileOpen }));
    };

    /**
     * Check if the account is already authenticated and get the account data
     * @returns {Promise<boolean>}
     * @private
     */
    _isUserAuthenticated = async () => {
        const token = await session.getToken();

        if (!token) {
            return false;
        }

        return true;
    };

    _removeRedirectWarning = () => {
        setMobileRedirectToken();

        this.setState({
            showDeviceWarning: false
        });
    };

    _triggerRedirect = () => {
        redirect();
    };

    _handleMenuClick = (event) => {
        this.setState({ anchorEl: event.currentTarget });
    };

    _handleCloseMenu = () => {
        this.setState({ anchorEl: null });
    };

    _logout = () => {
        this.props.dispatch(logout());
    };

    render() {
        const { component: Comp, user, notifications, whitelabel, ...rest } = this.props;
        const { showDeviceWarning } = this.state;

        const styles = {
            header: {
                display: 'flex',
                flex: 1,
                width: '100%',
                flexDirection: 'row',
                minHeight: 70,
                alignItems: 'center',
                borderBottom: `1px solid ${ Theme.colors.lightGrey }`
            },
            body: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                backgroundColor: Theme.colors.backgroundGrey,
                width: '100%'
            },
            breadcrumbsContainer: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1
            },
            headerIconContainer: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginRight: 40
            },
            headerIcon: {
                color: Theme.colors.mainTextGrey,
                cursor: 'pointer',
                marginLeft: 20,
                fontSize: 25
            },
            headerIconStale: {
                color: Theme.colors.mainTextGrey,
                cursor: 'pointer',
                marginLeft: 0,
                fontSize: 22
            },
            content: {
                flex: 1,
                height: '100vh',
                overflow: 'auto',
                position: 'relative',
                paddingTop: 25
            },
            logo: {
                width: 'auto',
                height: 40,
                marginLeft: 40
            },
            redirectContainer: {
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
            },
            redirect: {
                width: '93%',
                objectFit: 'contain',
                position: 'absolute',
                zIndex: 0
            },
            closeModalIcon: {
                cursor: 'pointer',
                display: 'block',
                right: '4%',
                top: '4%',
                width: '10%',
                height: '10%',
                position: 'absolute',
                color: Theme.colors.black,
                zIndex: 1
            }
        };

        if (this.state.loading) {
            return (
                <Loader whitelabel={whitelabel} />
            );
        }

        return (
            <Route
                {...rest}
                render={(props) => {
                    if (!this.state.isAuthenticated || !user) {
                        return (
                            <Redirect
                                to={ {
                                    pathname: '/login',
                                    state: { from: props.location }
                                } }
                            />
                        );
                    }

                    const email = user.email;
                    const isDependent = user.isDependent;
                    const name = `${ user.firstname } ${ user.lastname }`;

                    return (
                        <PrivateRouteContainer>
                            <div style={ styles.header }>
                                <DrawerIcon
                                    aria-label="Open drawer"
                                    onClick={this.handleDrawerToggle}
                                    sx={ styles.drawerIcon }
                                >
                                    <MenuIcon />
                                </DrawerIcon>

                                <LogoContainer>
                                    <Link to={`/`}>
                                        <img
                                            alt={ `${ whitelabel.name || 'Simply Benefits' } Logo` }
                                            src={ whitelabel.logo || logoImage }
                                            style={ { width: 'auto', height: 40, marginLeft: 40 } }
                                        />
                                    </Link>
                                </LogoContainer>

                                <div style={ styles.breadcrumbsContainer } />

                                <div style={ styles.headerIconContainer }>

                                    {
                                        !isDependent &&
                                        <Link to={`/notifications`} >
                                            <Badge
                                                color="secondary"
                                                badgeContent={ notifications ? notifications.length : 0 }
                                                invisible={ !notifications || notifications.length === 0 }
                                                sx={{ "& .MuiBadge-badge": { fontSize: 12, fontWeight: 900 } }}
                                            >
                                                <NotificationsIcon style={ styles.headerIcon } />
                                            </Badge>
                                        </Link>
                                    }

                                    <Link to={`/my-account`} >
                                        <Badge
                                            color="secondary"
                                            invisible={ true }
                                        >
                                            <GearIcon style={ { ...styles.headerIcon, marginLeft: 10 } } />
                                        </Badge>
                                    </Link>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }} onClick={this._handleMenuClick}>
                                        <Avatar
                                            name={name}
                                            size="40"
                                            textSizeRatio={2.5}
                                            round={true}
                                            color={whitelabel.primaryColor || Theme.colors.skyBlue}
                                            style={ styles.headerIcon }
                                        />

                                        <ArrowDropDownSharp style={ styles.headerIconStale } />
                                    </div>
                                    <MetaMenu
                                        anchorEl={this.state.anchorEl}
                                        open={Boolean(this.state.anchorEl)}
                                        onClose={this._handleCloseMenu}
                                        name={name}
                                        email={email}
                                        toggleModal={this._toggleModal}
                                        handleCloseMenu={this._handleCloseMenu}
                                        logout={this._logout}
                                        isMemberSite
                                    />

                                </div>
                            </div>

                            <div style={ styles.body }>
                                <SidebarContainer>
                                    <Drawer
                                        variant="temporary"
                                        anchor={'left'}
                                        open={this.state.mobileOpen}
                                        onClose={this.handleDrawerToggle}
                                    >
                                        <Sidebar closeSideBar={this.handleDrawerToggle} />
                                    </Drawer>
                                    <Drawer
                                        variant="permanent"
                                        open
                                        sx={ { [`& .MuiDrawer-paper`]: { height: '100vh', position: 'relative', backgroundColor: Theme.colors.surface } }}
                                    >
                                        <Sidebar />
                                    </Drawer>
                                </SidebarContainer>

                                {
                                    showDeviceWarning ?
                                        <div style={ styles.redirectContainer }>
                                            <CloseIcon
                                                style={ styles.closeModalIcon }
                                                onClick={ this._removeRedirectWarning }
                                            />
                                            <img
                                                alt="Redirect Prompt"
                                                src={ appRedirectImage }
                                                style={ styles.redirect }
                                                onClick={ this._triggerRedirect }
                                            />
                                        </div>
                                        :
                                        <div style={ styles.content } >
                                            <Comp {...props} />

                                            <DeviceWarningImage
                                                alt={ whitelabel.name ? `${ whitelabel.name } Watermark` : 'Watermark Logo' }
                                                src={ whitelabel.watermarkColor || waterMarkLogo }
                                                whitelabel={ whitelabel }
                                            />
                                        </div>
                                }
                            </div>
                        </PrivateRouteContainer>
                    );
                }}
            />
        );
    }
}

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    user: state.user.meta,
    notifications: state.user.notifications,
    whitelabel: state.whitelabel
});

export default (connect(mapStateToProps)(PrivateRoute));